import { useState } from 'react'

import { cn } from '@/lib/cn'
import { initials } from '@/lib/format'

const SIZES = {
  xs: 'size-5 text-[9px]',
  sm: 'size-6 text-[10px]',
  md: 'size-8 text-[11px]',
  lg: 'size-10 text-[13px]',
  xl: 'size-16 text-[20px]',
} as const

export function Avatar({
  src,
  name,
  size = 'md',
  className,
  ring,
}: {
  src?: string | null
  name: string
  size?: keyof typeof SIZES
  className?: string
  ring?: boolean
}) {
  const [failed, setFailed] = useState(false)

  return (
    <span
      className={cn(
        'bg-surface-sunken text-muted-foreground relative inline-flex shrink-0 items-center justify-center',
        'overflow-hidden rounded-full font-semibold select-none',
        ring && 'ring-background ring-2',
        SIZES[size],
        className,
      )}
      title={name}
    >
      {src && !failed ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      ) : (
        // Falling back to initials keeps the row height stable when Discord's
        // CDN is slow or the avatar 404s.
        initials(name)
      )}
    </span>
  )
}

export function AvatarStack({
  people,
  max = 4,
  size = 'sm',
}: {
  people: { id: string; name: string; avatar_url?: string | null }[]
  max?: number
  size?: keyof typeof SIZES
}) {
  const shown = people.slice(0, max)
  const overflow = people.length - shown.length

  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((person) => (
        <Avatar key={person.id} src={person.avatar_url} name={person.name} size={size} ring />
      ))}
      {overflow > 0 && (
        <span
          className={cn(
            'bg-surface-sunken text-muted-foreground ring-background relative inline-flex',
            'items-center justify-center rounded-full font-semibold ring-2',
            SIZES[size],
          )}
        >
          +{overflow}
        </span>
      )}
    </div>
  )
}
